import React from 'react';
import {
  ArrowLeft,
  User,
  FileText,
  Calendar,
  AlertCircle,
  Download,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

export default function PersonalActionDetails({ action, onClose }) {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-DO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoriaColor = (categoria) => {
    const colors = {
      'administrativa': 'bg-blue-100 text-blue-700',
      'disciplinaria':  'bg-red-100 text-red-700',
      'contractual':    'bg-blue-100 text-blue-700',
      'salarial':       'bg-green-100 text-green-700',
    };
    return colors[categoria] || 'bg-gray-100 text-gray-600';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      'activo': <CheckCircle className="w-6 h-6 text-green-600" />,
      'revocado': <XCircle className="w-6 h-6 text-red-600" />,
      'completado': <CheckCircle className="w-6 h-6 text-blue-600" />
    };
    return icons[estado] || <Clock className="w-6 h-6 text-gray-600" />;
  };

  const renderDatosAccion = () => {
    if (!action.datos_accion || Object.keys(action.datos_accion).length === 0) {
      return <p className="text-gray-500 text-sm">No hay datos específicos registrados</p>;
    }

    const datos = action.datos_accion;

    return (
      <div className="space-y-3">
        {Object.entries(datos)
          // Filtrar campos que contienen IDs (position_id, department_id, etc.)
          .filter(([key]) => !key.includes('_id'))
          .map(([key, value]) => {
            // Formatear el nombre del campo
            const fieldName = key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase());

            // Formatear el valor
            let displayValue = value;
            if (typeof value === 'boolean') {
              displayValue = value ? 'Sí' : 'No';
            } else if (typeof value === 'number') {
              if (key.includes('salario') || key.includes('monto')) {
                displayValue = `RD$ ${value.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
              } else if (key.includes('porcentaje')) {
                displayValue = `${value}%`;
              }
            }

            return (
              <div key={key} className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">{fieldName}:</span>
                <span className="text-sm text-gray-900">{displayValue}</span>
              </div>
            );
          })}
      </div>
    );
  };

  const estadoBadge = (() => {
    const map = {
      pendiente:  { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Pendiente'  },
      aprobado:   { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Aprobado'   },
      rechazado:  { bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Rechazado'  },
      revocado:   { bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400',   label: 'Revocado'   },
      completado: { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Completado' },
    };
    return map[action.estado] || map.completado;
  })();

  return (
    <div className="space-y-4">

      {/* ── Back button ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver a Acciones de Personal
        </button>
      </div>

      {/* ── Action card header ────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{action.titulo}</h2>
            <p className="text-sm text-gray-400 mt-0.5">{action.tipo_accion_nombre}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${estadoBadge.bg} ${estadoBadge.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${estadoBadge.dot}`} />
                {estadoBadge.label}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoriaColor(action.tipo_accion_categoria)}`}>
                {action.tipo_accion_categoria || 'Sin categoría'}
              </span>
              {action.numero_accion && (
                <span className="text-xs font-mono text-blue-600">{action.numero_accion}</span>
              )}
            </div>
          </div>
          {action.empleado_nombre && (
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-semibold text-gray-900">{action.empleado_nombre}</p>
              <p className="text-xs text-gray-400">{action.empleado_codigo}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
          {/* Información del Empleado */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Empleado
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
              {action.empleado_foto ? (
                <img
                  src={action.empleado_foto}
                  alt={action.empleado_nombre}
                  className="w-16 h-16 rounded-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-xl"
                style={{ display: action.empleado_foto ? 'none' : 'flex' }}
              >
                {action.empleado_nombre?.charAt(0) || '?'}
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{action.empleado_nombre}</p>
                <p className="text-sm text-gray-600">Código: {action.empleado_codigo}</p>
              </div>
            </div>
          </div>

          {/* Detalles de la Acción */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Detalles de la Acción
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Número de Acción:</span>
                <span className="text-sm text-gray-900">{action.numero_accion || 'N/A'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Categoría:</span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getCategoriaColor(action.tipo_accion_categoria)}`}>
                  {action.tipo_accion_categoria || 'Sin categoría'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Estado:</span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  action.estado === 'activo' ? 'bg-green-100 text-green-800' :
                  action.estado === 'revocado' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {action.estado.toUpperCase()}
                </span>
              </div>

              {action.descripcion && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-1">Descripción:</p>
                  <p className="text-sm text-gray-900">{action.descripcion}</p>
                </div>
              )}
            </div>
          </div>

          {/* Fechas */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Fechas
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Fecha Efectiva:</span>
                <span className="text-sm text-gray-900">{formatDate(action.fecha_efectiva)}</span>
              </div>

              {action.fecha_fin && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Fecha Fin:</span>
                  <span className="text-sm text-gray-900">{formatDate(action.fecha_fin)}</span>
                </div>
              )}

              {action.dias_restantes !== null && action.dias_restantes !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Días Restantes:</span>
                  <span className={`text-sm font-semibold ${
                    action.dias_restantes < 0 ? 'text-red-600' :
                    action.dias_restantes <= 7 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {action.dias_restantes < 0 ? 'Vencido' : `${action.dias_restantes} días`}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Fecha de Registro:</span>
                <span className="text-sm text-gray-900">{formatDate(action.created_at?.split('T')[0])}</span>
              </div>
            </div>
          </div>

          {/* Datos Específicos */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Datos Específicos
            </h3>
            <div className="bg-gray-50 rounded-xl p-4">
              {renderDatosAccion()}
            </div>
          </div>

          {/* Documento */}
          {action.documento_url && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-600" />
                Documento Adjunto
              </h3>
              <a
                href={action.documento_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Descargar Documento
              </a>
            </div>
          )}

          {/* Revocación */}
          {action.estado === 'revocado' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Información de Revocación
              </h3>
              <div className="bg-red-50 rounded-xl p-4 space-y-3 border border-red-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-red-800">Fecha de Revocación:</span>
                  <span className="text-sm text-red-900">{formatDate(action.fecha_revocacion)}</span>
                </div>
                {action.motivo_revocacion && (
                  <div className="pt-2 border-t border-red-200">
                    <p className="text-sm font-medium text-red-800 mb-1">Motivo:</p>
                    <p className="text-sm text-red-900">{action.motivo_revocacion}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Auditoría Completa */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Historial de Auditoría
            </h3>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="space-y-4">
                {/* Creación/Solicitud */}
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900">📝 Solicitado</span>
                    <span className="text-xs text-gray-500">
                      {new Date(action.created_at).toLocaleString('es-DO', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    👤 <strong>{action.creado_por || 'Sistema'}</strong> creó esta solicitud
                  </p>
                </div>

                {/* Aprobación */}
                {action.estado === 'aprobado' && action.approved_at && (
                  <div className="border-l-4 border-green-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-green-900">✅ Aprobado</span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.approved_at).toLocaleString('es-DO', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      👤 <strong>{action.aprobado_por || 'Sistema'}</strong> aprobó y aplicó los cambios al sistema
                    </p>
                  </div>
                )}

                {/* Rechazo */}
                {action.estado === 'rechazado' && action.rejected_at && (
                  <div className="border-l-4 border-red-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-red-900">❌ Rechazado</span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.rejected_at).toLocaleString('es-DO', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      👤 <strong>{action.rechazado_por || 'Sistema'}</strong> rechazó esta solicitud
                    </p>
                    {action.rechazo_motivo && (
                      <div className="mt-2 bg-red-50 rounded p-2">
                        <p className="text-xs text-red-800">
                          <strong>Motivo:</strong> {action.rechazo_motivo}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Actualización */}
                {action.actualizado_por && action.updated_at !== action.created_at && (
                  <div className="border-l-4 border-purple-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-purple-900">🔄 Actualizado</span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.updated_at).toLocaleString('es-DO', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      👤 <strong>{action.actualizado_por}</strong> modificó esta acción
                    </p>
                  </div>
                )}

                {/* Estado Pendiente */}
                {action.estado === 'pendiente' && (
                  <div className="border-l-4 border-yellow-500 pl-4 py-2 bg-yellow-50">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-yellow-900">⏳ Pendiente de Aprobación</span>
                    </div>
                    <p className="text-xs text-yellow-700 mt-1">
                      Esta acción está esperando aprobación de un administrador
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

    </div>
  );
}
